import {Component, EventEmitter, forwardRef, Injector, Input, OnDestroy, Output, SimpleChanges, ViewChild} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    NgControl,
    TouchedChangeEvent,
    ValidationErrors,
    Validator
} from '@angular/forms';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {environment} from "@env/environment";

@Component({
    selector: 'creditsnap-ssn',
    templateUrl: './ssn.component.html',
    styleUrls: ['./ssn.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => SsnComponent),
        multi: true
    }, {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => SsnComponent),
        multi: true
    }]
})

export class SsnComponent implements ControlValueAccessor, Validator, OnDestroy {
    /**
     * Holds the current value of the slider
     */
    value: string;
    @Input() ssnValue = '';
    @Input() name = 'ssn';
    @Input() id = 'ssn';
    @Input() hide = true;
    @Input() disabled = false;
    @Input() readonly = false;
    @Input() touched = false;
    @Input('data-test-id') testId: string = 'ssn-input';
    @Input() required = true;

    @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
    @ViewChild('ssn', {read: NgControl}) ssnEleRef: NgControl | undefined;

    private outerTouchedSub: Subscription | undefined;

    constructor(private injector: Injector) {
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return this.addCustomValidator(control);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.touched && changes.touched.currentValue) {
            this.ssnEleRef.control.markAsTouched();
        }
    }

    ngAfterViewInit(): void {
        this.addCustomValidatorToElement();
        this.syncTouchedWithOuterControl();
    }

    ngOnDestroy(): void {
        this.outerTouchedSub?.unsubscribe();
    }

    /**
     * LP-2681: the parent form only knows the outer control bound on `<creditsnap-ssn>` (ngModel /
     * formControlName). Submit handlers such as `tsSubmitIfValid` mark that outer control touched,
     * but the mat-error in this template reads the inner input's control, so an empty SSN was
     * blocking submit without showing "Please enter your Social Security number.". Mirror the
     * outer touched state onto the inner control so the error becomes visible.
     */
    private syncTouchedWithOuterControl() {
        const outerControl = this.injector.get(NgControl, null, {self: true, optional: true})?.control;
        if (!outerControl) {
            return;
        }
        if (outerControl.touched) {
            this.touch();
        }
        this.outerTouchedSub = outerControl.events.pipe(
            filter((event): event is TouchedChangeEvent => event instanceof TouchedChangeEvent)
        ).subscribe(event => {
            if (event.touched) {
                this.touch();
            } else {
                this.ssnEleRef?.control.markAsUntouched();
            }
        });
    }

    /**
     * Invoked when the model has been changed
     */
    onChange(_: string) {
        console.log(_);
    }

    /**
     * Invoked when the model has been touched
     */
    onTouched: () => void = () => {
    }

    /**
     * Method that is invoked on an update of a model.
     */
    updateChanges() {
        this.onChange(this.value);
    }

    ///////////////
    // OVERRIDES //
    ///////////////

    /**
     * Writes a new item to the element.
     * @param value the value
     */
    writeValue(value: string): void {
        this.value = value;
        this.updateChanges();

    }

    /**
     * Registers a callback function that should be called when the control's value changes in the UI.
     * @param fn
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Registers a callback function that should be called when the control receives a blur event.
     * @param fn
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    addCustomValidatorToElement() {
        this.ssnEleRef.control.addValidators((control: AbstractControl): ValidationErrors | null => this.addCustomValidator(control));
    }

    touch() {
        this.ssnEleRef?.control.markAsTouched();
    }

    addCustomValidator(control: AbstractControl): ValidationErrors | null {
        let error = null;
        if (this.required && !this.value) {
            error = {'required': true};
        } else if (!this.value) {
            error = null;
        } else if (this.value.toString().indexOf('XXX') >= 0) {
            error = null;
        } else {
            const ssn = this.value ? this.value.toString().replace(/([\D,\s,.€()_-])+/g, '') : '';
            // if (!(ssn.length === 9 || ssn.length === 10)) {
            if (ssn && ssn.length !== 9) {
                error = {'invalid': true};
            }
            // If SSN's length more than 5 and if same digit is repeated more than 5 times than SSN is not valid
            else if (ssn && ssn.length > 5 && this.checkRepeatedNumber(ssn) > 6) {
                error = {'invalid': true};

            } else if (ssn && ssn.length === 9) {
                let [firstSet, secondSet, thirdSet] = this.value.toString().split('-');
                if (!thirdSet) {
                    firstSet = ssn.substring(0, 3);
                    secondSet = ssn.substring(3, 5);
                    thirdSet = ssn.substring(5, 9);
                }
                // 999021 is the sales demo tenant on prod: allow bureau test SSNs (666-xx-xxxx)
                const allowTestSsn = environment.type === 'TEST' || environment.institutionId === '999021';
                const checkNumber = ['000', ...(!allowTestSsn ? ['666'] : [])];
                if (checkNumber.includes(firstSet) || (Number(firstSet) >= 900 && Number(firstSet) <= 999)) {
                    error = {'invalid': true};
                }
                if (secondSet === '00') {
                    error = {'invalid': true};
                }
                if (thirdSet === '0000') {
                    error = {'invalid': true};
                }
            }
        }

        if (this.ssnEleRef) {
            this.ssnEleRef.control.setErrors(error);
        }
        return error;
    }

    private checkRepeatedNumber(str) {
        const strAr = String(str).split('');
        const mostFrequentNumber = strAr.sort((a, b) =>
            strAr.filter(v => v === a).length
            - strAr.filter(v => v === b).length
        )[strAr.length - 1];

        return strAr.filter(x => x === mostFrequentNumber).length;
    }


    onChangeSSN() {
        let newVal = (this.value || '').toString().replace(/\D/g, '');
        if (newVal.length === 0) {
            newVal = '';
        } else if (newVal.length <= 3) {
            newVal = newVal.replace(/^(\d{0,3})/, '$1');
        } else if (newVal.length <= 6) {
            newVal = newVal.replace(/^(\d{0,3})(\d{0,2})/, '$1-$2');
        } else if (newVal.length <= 9) {
            newVal = newVal.replace(/^(\d{0,3})(\d{0,2})(\d{0,4})/, '$1-$2-$3');
        } else {
            newVal = newVal.substring(0, 9);
            newVal = newVal.replace(/^(\d{0,3})(\d{0,2})(\d{0,4})/, '$1-$2-$3');
        }
        this.writeValue(newVal);
    }
}
